import React, { useMemo, useState } from "react";
import { useRoom } from "./RoomProvider";
import { useApprovalCalls } from "../../hooks/useApprovalCalls";

/**
 * SystemBannerStack — the ONE home for top-of-screen system banners.
 *
 * Truth Audit finding: ConnectionBanner and ApprovalCallBanner used to be
 * two independently absolute-positioned pills both anchored at ~top:60-64px,
 * centered — the instant both were true at once (SSE dropped WHILE Nova was
 * mid Away-Mode call) they rendered directly on top of each other, unreadable.
 * Same class of bug the left rail already fixed for its 3 widgets. Fixed the
 * same way: one coordinated column, each banner just adds a row.
 */
export default function SystemBannerStack({ showApprovalCalls = true }) {
  const { tripsConnected, autoResolvedAlerts = [], undoAutoAlert, kaizenPipelineAlert, zonePressureAlert } = useRoom();
  const { calling } = useApprovalCalls();
  // Zone pressure is a soft signal (not a failure) — dismissible, and the
  // dismissal only re-arms once the set of short zones actually changes,
  // so it can't nag every poll tick while nothing's new.
  const [dismissedZoneSignature, setDismissedZoneSignature] = useState(null);
  const zoneSignature = useMemo(
    () => (zonePressureAlert?.zones || []).map(z => z.zone_id).sort().join(","),
    [zonePressureAlert]
  );

  const rows = [];
  if (kaizenPipelineAlert?.has_active_failure) {
    const top = kaizenPipelineAlert.incidents?.[0];
    rows.push({
      key: "kaizen-alert",
      testId: "floor-kaizen-alert-banner",
      dotClass: "kaizen-alert-dot",
      node: (
        <span className="kaizen-alert-text">
          Kaizen pipeline issue · <b>{top?.label || "silent failure detected"}</b>
          {kaizenPipelineAlert.count > 1 ? ` · +${kaizenPipelineAlert.count - 1} more` : ""}
        </span>
      ),
    });
  }
  if (zonePressureAlert && zoneSignature && zoneSignature !== dismissedZoneSignature) {
    const zones = zonePressureAlert.zones;
    rows.push({
      key: "zone-pressure",
      testId: "floor-zone-pressure-banner",
      dotClass: "zone-pressure-dot",
      node: (
        <span className="zone-pressure-text">
          Zone short on supply · <b>{zones[0].zone_name}</b> ({zones[0].supply_count} drivers / {zones[0].trip_count} trips)
          {zones.length > 1 ? ` · +${zones.length - 1} more` : ""}
          <button className="zone-pressure-dismiss-btn" data-testid="floor-zone-pressure-dismiss-btn"
                  onClick={() => setDismissedZoneSignature(zoneSignature)}>Dismiss</button>
        </span>
      ),
    });
  }
  if (!tripsConnected) {
    rows.push({
      key: "connection",
      testId: "floor-conn-banner",
      dotClass: "conn-dot",
      node: <span className="conn-text">live stream reconnecting · polling every 30s in the meantime</span>,
    });
  }
  if (autoResolvedAlerts.length) {
    const top = autoResolvedAlerts[0];
    rows.push({
      key: "nova-autonomy",
      testId: "floor-autonomy-banner",
      dotClass: "autonomy-dot",
      node: (
        <span className="autonomy-text">
          Nova auto-reassigned <b>{top.rider_name || "a trip"}</b> to <b>{top.suggested_driver_name || "a driver"}</b> · training wheels
          {autoResolvedAlerts.length > 1 ? ` · +${autoResolvedAlerts.length - 1} more` : ""}
          <button className="autonomy-undo-btn" data-testid="floor-autonomy-undo-btn"
                  onClick={() => undoAutoAlert(top.alert_id)}>Undo</button>
        </span>
      ),
    });
  }
  if (showApprovalCalls && calling.length) {
    const top = calling[0];
    const target = top.escalation_state?.last_target || "the away roster";
    rows.push({
      key: "approval-call",
      testId: "floor-approval-call-banner",
      dotClass: "acb-dot",
      node: (
        <span className="acb-text">
          Nova is trying to reach <b>{target}</b> · {top.label}
          {calling.length > 1 ? ` · +${calling.length - 1} more` : ""}
        </span>
      ),
    });
  }

  if (!rows.length) return null;

  return (
    <div className="system-banner-stack" data-testid="system-banner-stack">
      {rows.map((r, i) => (
        <div key={r.key} className={`sbs-row sbs-row--${r.key}`} data-testid={r.testId}
             style={{ animationDelay: `${i * 60}ms` }}>
          <span className={r.dotClass} />
          {r.node}
        </div>
      ))}
    </div>
  );
}
